import React, { useId } from 'react'
import Chart from 'react-google-charts'
import { useSelector } from 'react-redux'
import { getYou, getShowWeather } from '../redux/options/selectors' 
import { time2seconds } from '../tools/Time'
import { getUnique } from '../tools/Array'
import { getCarColours, isHighlighted } from '../tools/CarColours'
import ChartContainer from './ChartContainer'
import WeatherStrip, { WEATHER_HEIGHT, getTimeToAxis } from './WeatherStrip'
import WeatherForecast from './WeatherForecast'
import { useChartTooltip } from './ChartTooltip'

// non numerical default time, will be sorted to the back if the list
const default_time = 'xxx:xx.xxx'

/**
 * Height in pixels of each position in the plot
 * @type {number}
 */
const ROW_HEIGHT = 23

/**
 * Get the height of the chart, so a page can keep its room before the chart is drawn
 * @param {number} cars number of cars in the chart
 * @param {boolean} weather whether the weather is shown above the plot
 * @param {number} rowHeight height in pixels of each position
 * @returns {number} height in pixels
 */
export const getPositionChartHeight = ( cars, weather = false, rowHeight = ROW_HEIGHT ) => Math.max( 60, cars * rowHeight ) + 20 + ( weather ? WEATHER_HEIGHT : 0 )

/**
 * Remove all rows without position change
 * @param {*} rows 
 */
const shortenData = rows => 
{
  // remember last entry
  let last_hash = ''
  // filter out unique values
  return rows.filter( row => {
    // make sort of a hash string to compare uniqueness
    const row_hash = row.slice( 1 ).map( r => r.pos_hash ).join( '-' )
    // only add if this entry isn't the same as the previous one
    if ( row_hash !== last_hash )
    {
      last_hash = row_hash
      return true
    }
    return false
  }) 
}

/**
 * // update laptime of a specific car / row
 * @param {*} row 
 * @returns 
 */
const updateLaptime = ( row, last_time, last_pos ) =>
{
  // go over all previously set times
  last_time.forEach( ( last, index ) => 
  {
    // find the car
    if ( String( last.car ) === String( row.car ) )
    {
      // maybe update 
      if ( row.laptime < last.laptime || last.laptime === default_time )
      {        
        // clone the entire row
        last_time[ index ] = { ...row }
      }
      // stop here
      return
    }
  })

  // sort on laptime so the fastest is first
  last_time.sort( ( a, b ) => a.laptime > b.laptime ? 1 : -1 )

  // add last positions based on laptime
  last_time.forEach( ( last, index ) =>
  {
    if ( last.laptime !== default_time )
    {
      // add position and merge in row data for tooltip
      last_pos[ last.car ] = { 
        // position of the car
        position: index + 1,
        // car number
        car: last.car,
        // hash based on position
        pos_hash: `${last.car}-${index+1}`,
        // hash based on position and laptime
        time_hash: `${last.car}-${index+1}-${last.laptime}`,
        // raw data
        data: last
      }
    }
  })

  // just return the values, maintain (car) order hopefully
  return Object.values( last_pos )
}

/**
 * Get graph header
 * @param {*} param0 
 * @returns 
 */
const getHeader = cars => 
{
  // add header row, position + car numbers as strings
  let header = [ 'Lap' ]
  cars.forEach( c => {
    // add car number row
    header.push( String( c ) )
  })
  return header
}

/**
 * get car numbers sorted on position from the last row
 * @param {*} rows 
 */
const getSortedCarNums = ( rows ) => 
{
  // get a copy of the last row
  const last_row = [ ...rows[ rows.length - 1 ] ]
  // remove the first item, this is the lapnumber or x-axis index
  last_row.shift()
  // sort on position
  const last_sorted = last_row.sort( ( a, b ) => a.position - b.position )
  // get car numbers
  const cars_sorted = last_sorted.map( l => l.car )
  // return the cars sorted on position
  return cars_sorted
}

/** 
 * Get data for the graph
 * @param {*} cars 
 * @param {*} rows 
 */
const getGraphData = ( cars, rows ) =>
{
  // put it together, ALWAYS add the header column
  let data = [ getHeader( cars ) ]
  rows.forEach( ( row, index ) => 
  {
    // prep data row; lap, ( position, toolip ) * cars
    let d = []

    // go over each element in the sliced row
    row.forEach( r => {
      if ( Number.isInteger( r ) )
      {
        // add index, not lapnumber as lapnumber makes no sense to the user
        // this is the first or header column
        d.push( index )
      }
      else
      {
        // add negative position, we revert this in the v-axis ticks
        // otherwise position numbers are upside down in the left legend of the graph
        d.push( { v: -r.position, f: `${-r.position}` } )
      }
    })
    
    // add data row
    data.push( d )
  })

  return data
}

/**
 * Fix the legend after render: show the car numbers on the right in the order of their positions
 * @param {string} id id of the chart container
 * @param {Array<number|string>} sorted_cars car numbers sorted on position
 * @returns {void}
 */
const fixLegend = ( id, sorted_cars ) =>
{
        // get chart container div
  const el = document.getElementById( id ),
        // first svg is the root of the chart
        svg = el ? el.querySelector( 'svg' ) : null,
        // root leven groups contain left- and rightside legends and the graph itself
        g1 = el ? el.querySelectorAll( 'svg > g' ) : null,
        // car numbers on the right side
        nums = g1 ? g1[ 0 ] : null,
        // graph in the middle
        graph = g1 ? g1[ 1 ] : null,
        // get graph groups containing the points on the chart
        g2 = graph ? graph.querySelectorAll( ':scope > g' ) : null,
        // get last position element so we can extract the car numbers later
        pos = g2 ? g2[ g2.length - 1 ] : null


  if ( nums )
  {
    nums.style.display = 'none'
  }

  if ( pos && svg )
  {
          // clone position element
    const newpos = pos.cloneNode( true ),
          // get all text nodes as an array
          texts = Array.from( newpos.querySelectorAll( 'text' ) ),
          // get y coordinates list
          ys = texts.map( text => text.getAttribute( 'y' ) )

    // sort y coordinates
    ys.sort( ( a, b ) => a - b )

    // move text nodes to the right of the grapth and add car numbers in the correct order
    sorted_cars.forEach( ( car, index ) => {
      const text = texts[ index ]
      text.setAttribute( 'y', ys[ index ] )
      text.setAttribute( 'x', svg.clientWidth )
      text.textContent = car
    } )

    svg.appendChild( newpos )
  }
}

/**
 * Render the position chart
 * @param {Object} props component props
 * @param {Array<Object>} props.rows laps of the group
 * @param {number} props.max_rows number of position changes to show, 0 for all
 * @param {number|string} props.car chosen car, drawn in red like yours
 * @param {boolean} props.shorten whether to leave out laps that did not change the order
 * @param {boolean|null} props.weather whether the weather is shown, null to follow the preferences
 * @param {boolean} props.zoom whether the chart can be zoomed and panned
 * @param {boolean} props.credit whether the weather forecast and its source are shown below the
 *   chart, left out where the page shows them once
 * @param {boolean} props.personal whether your car is drawn in red, not on a public screen
 * @param {number} props.rowHeight height in pixels of each position
 * @returns {JSX.Element|null} chart, nothing without enough laps
 */
const PositionChart = ( { rows = [], max_rows = 20, car = '', shorten = true, weather = null, zoom = true, credit = true, personal = true, rowHeight = ROW_HEIGHT } ) =>
{
  const yourCar = useSelector( getYou )
  const you = personal ? yourCar : ''
  const preferredWeather = useSelector( getShowWeather )
  const showWeather = weather === null ? preferredWeather : weather
  // each chart has its own container, so a page can show several
  const id = 'position-chart-' + useId()
  // the lap of a clicked point
  const { ref, onClick, onChartReady, close, tooltip } = useChartTooltip()

  // stop here if there's no data
  if ( !rows || rows.length === 1 ) return null

  // remove laps with penalties or remarks
  const valid = rows.filter( row => row.penalty === 0 && row.comment === '' )

  // stop here if there's no valid data
  if ( !valid || valid.length === 1 ) return null

  // sort by time so oldest item is first
  valid.sort( (a , b) => a.time > b.time ? 1 : -1 )

  // get a list of unique cars
  const cars = getUnique( valid, 'car' )

  // more cars = more rows
  // max_rows = Math.max( max_rows, cars.length )

  // stop here if there's not enough cars
  if ( !cars || cars.length <= 1 ) return null

  // add last known laptime for each car (make if really big), use an array so we can sort it
  const last_time = cars.map( c => ( { car: c, laptime: default_time } ) )

  // last position is an object, doesn't need nor want sorting
  const last_pos = {}
  cars.forEach( c => last_pos[ c ] = { 
    position: cars.length, 
    car: c,
    pos_hash: `${c}-0`,
    time_hash: `${c}-0-0:00.000`,
    data: null 
  } )

  // each lap, add laptime for the car that did the lap & reacalculate each cars position
  const positions = valid.map( ( row, index ) => [
    // add lapnumber first
    ...[ index + 1 ],
    // add cars and positions next
    ...updateLaptime( row, last_time, last_pos )
  ] )

  // shorten array by only showing laptimes that changed the order
  let uniqued = shorten ? shortenData( positions ) : positions

  // maybe limit number of rows to show
  const sliced = max_rows ? uniqued.slice( -1 * Math.min( max_rows + 1, uniqued.length ) ) : uniqued

  // get graph data
  const data = getGraphData( cars, sliced )

        // point size ( and top / bottom )
  const pointSize = 2,
        lineWidth = 1,
        // room to the left and right
        leftSide = 30,
        rightSide = 30

  // height of the whole chart, based on the number of cars
  const height = getPositionChartHeight( cars.length, showWeather, rowHeight )

  // colour per car, the same as in the laptimes chart; your car or the chosen car highlighted
  const colours = getCarColours( rows, [ car, you ] )
  const series = cars.map( c => isHighlighted( colours, c )
    ? { color: colours[ String( c ) ], pointSize: pointSize * 2, lineWidth: lineWidth * 2 }
    : { color: colours[ String( c ) ] }
  )

  // room above the plot for the weather, and below it
  const top = 10 + ( showWeather ? WEATHER_HEIGHT : 0 ),
        bottom = 10

  // car numbers for the legend, sorted on their last position
  const sorted_cars = getSortedCarNums( sliced )

  // time of each shown position change, its first item is the number of the lap that made it
  const times = sliced.map( row => time2seconds( valid[ row[ 0 ] - 1 ].time ) )

  // map v-axis tick
  const vAxisTicks = cars => {
     return cars.map( ( v, i ) => {
      const f = i + 1
      return { v: -f, f: `${f}`}
    } )
  }

  /**
   * Get the lap of a point: its row is a shown position change, its column a car
   * @param {number} row row of the chart
   * @param {number} column column of the chart
   * @returns {Object|null} lap, null for a car without one yet
   */
  const getLap = ( row, column ) =>
  {
    const place = sliced[ row ] ? sliced[ row ][ column ] : null
    return place && place.data ? place.data : null
  }

  // render the chart
  return (
    <div className="chart-tooltip-area" ref={ ref } onClick={ onClick }>
      { tooltip }
      <ChartContainer
        onViewChange={ close }
        className="linechart chart position-chart"
        id={ id }
        min={ 0 }
        max={ sliced.length - 1 }
        minRange={ 2 }
        left={ leftSide }
        right={ rightSide }
        footer={ showWeather && credit ? <WeatherForecast credit/> : null }
        zoom={ zoom }
      >
        { view => (
          <React.Fragment>
            { showWeather && (
              <WeatherStrip
                toX={ getTimeToAxis( times, times.map( ( time, index ) => index ) ) }
                from={ times[ 0 ] }
                to={ times[ times.length - 1 ] }
                view={ view || { min: 0, max: sliced.length - 1 } }
                left={ leftSide }
                right={ rightSide }
                top={ top }
                bottom={ bottom }
              />
            ) }
            <Chart 
              width={ '100%' }
              height={ height + 'px' }
              chartType="LineChart" 

              chartEvents={[
                {
                  eventName: 'ready',
                  callback: ( event ) =>
                  {
                    fixLegend( id, sorted_cars )
                    onChartReady( getLap )( event )
                  }
                }
              ]}

              options={ { 
                height: height,
                pointSize: pointSize,
                lineWidth: lineWidth,
                series: series,
                vAxis: { 
                  ticks: vAxisTicks( cars ), 
                  title: '',
                  gridlines: {
                    color: '#eee'
                  },
                },
                hAxis: { 
                  ticks: [], 
                  title: '',
                  viewWindow: view || undefined
                },
                chartArea: { 
                  left: leftSide,
                  top: top, 
                  right: rightSide,
                  bottom: bottom,
                },
                tooltip: { 
                  trigger: 'none'
                }
              } }

              data={ data } 
            />
          </React.Fragment>
        ) }
      </ChartContainer>
    </div>
  )
}

export default PositionChart